import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectTrigger,
  Typography,
} from '@growdash/design-system/atoms';
import { cn } from '@growdash/design-system/utils/helpers.js';
import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import { Brand } from '@growdash/growintel/generated';
import {
  useDebounce,
  useGrowintelService,
  useWatchlist,
} from '@growdash/growintel/hooks';
import {
  useVirtualizer,
  type VirtualItem as VirtualizerItem,
} from '@tanstack/react-virtual';
import { Bookmark, Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  BrandSourceCategory,
  useBrandQueries,
  useCachedBrands,
  useVirtualItems,
  VirtualItem,
} from './hooks/use-select-brand-data';

// Types
interface SelectBrandProps {
  className?: string;
  value?: string;
  onValueChange: (brand: Brand) => void;
  disabled?: boolean;
  city?: string;
  cuisine?: string | null;
  area?: string;
  renderedCategories?: BrandSourceCategory[];
}

interface BrandOptionProps {
  brand: Brand;
  isSelected?: boolean;
  brandSourceCategory: BrandSourceCategory;
  onValueChange: (brand: Brand) => void;
  className?: string;
}

// Constants
const DEBOUNCE_DELAY = 300;
const MAX_HEIGHT = 300;
const OVERSCAN = 5;

// Sub-components
function BrandOption({
  brand,
  isSelected = false,
  brandSourceCategory,
  onValueChange,
  className = '',
}: BrandOptionProps) {
  const handleClick = useCallback(() => {
    onValueChange(brand);
  }, [brand, onValueChange]);

  return (
    <div
      onClick={handleClick}
      className={cn(
        'animate-fade-in h-9 cursor-pointer hover:bg-tertiary flex items-center rounded-md',
        { 'bg-tertiary': isSelected },
        className,
      )}
    >
      <div className="flex items-center gap-2 px-2 h-9">
        {brandSourceCategory === 'watchlist' ? (
          <Bookmark className="text-support-orange fill-support-orange w-4 min-w-4" />
        ) : (
          <GrowintelImage
            type="brand"
            className="min-w-4 w-4 h-4 rounded-full bg-quarternary"
            src={brand?.brand_image ?? ''}
          />
        )}
        <Typography className="text-sm font-medium">
          {brand.brand_name}
        </Typography>
      </div>
      {isSelected && <Check className="ml-auto h-4 w-4 mx-2" />}
    </div>
  );
}

function LoadingItem({
  setRef,
}: {
  setRef: (node: HTMLDivElement | null) => void;
}) {
  return (
    <div ref={setRef} className="flex py-4 gap-2 items-center px-2">
      <Loader2 size={14} className="animate-spin" />
      <Label>Loading</Label>
    </div>
  );
}

// Main component
export default function SelectBrand({
  className = '',
  value,
  onValueChange,
  disabled,
  city,
  cuisine,
  area,
  renderedCategories = ['other', 'watchlist'],
}: SelectBrandProps) {
  // State
  const [searchInput, setSearchInput] = useState('');
  const [open, setOpen] = useState(false);
  const [showAllWatchlist, setShowAllWatchlist] = useState(false);

  // Hooks
  const { watchlist, isWatchlistLoading } = useWatchlist();
  const service = useGrowintelService();
  const { ref, inView } = useInView();
  const debouncedSearch = useDebounce(searchInput, DEBOUNCE_DELAY);
  const parentRef = useRef<HTMLDivElement>(null);

  // Queries
  const { initialQuery, searchQuery, activeQuery, brands } = useBrandQueries(
    service,
    city,
    area,
    cuisine || undefined,
    debouncedSearch,
    renderedCategories,
  );

  // Cached brands
  const cachedBrands = useCachedBrands(
    initialQuery.data?.pages.map((p) => p.items),
    searchQuery.data?.pages.map((p) => p.items),
  );

  // Virtual items
  const virtualItems = useVirtualItems(
    watchlist || [],
    brands,
    searchInput,
    cuisine,
    renderedCategories,
    value,
    activeQuery,
    showAllWatchlist,
  );

  // Virtualization
  const rowVirtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: OVERSCAN,
  });

  // Computed values
  const selectedBrand = useMemo(() => {
    if (!value) return null;
    return (
      watchlist?.find((b: Brand) => b.id === value) ??
      cachedBrands.find((b: Brand) => b.id === value)
    );
  }, [value, watchlist, cachedBrands]);

  const hasOptionsLoaded = useMemo(() => {
    return renderedCategories.every((category) => {
      switch (category) {
        case 'watchlist':
          return (
            renderedCategories.includes('watchlist') && !isWatchlistLoading
          );
        case 'other':
          return !!service;
        default:
          return true;
      }
    });
  }, [renderedCategories, isWatchlistLoading, service]);

  // Event handlers
  const handleSelectBrand = useCallback(
    (brand: Brand) => {
      onValueChange(brand);
      setOpen(false);
    },
    [onValueChange],
  );

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchInput('');
      setShowAllWatchlist(false);
    }
  }, []);

  const renderSelectedOption = useCallback(() => {
    if (!selectedBrand) return null;

    return (
      <div key={selectedBrand?.id} className="flex items-center gap-2 max-w-[240px]">
        <GrowintelImage
          key={value}
          className="w-4 h-4 rounded-full flex-shrink-0"
          src={selectedBrand.brand_image ?? ''}
        />
        <span className="truncate">{selectedBrand.brand_name}</span>
      </div>
    );
  }, [selectedBrand, value]);

  const renderVirtualItem = useCallback(
    (item: VirtualItem) => {
      switch (item.type) {
        case 'brand':
          return (
            <BrandOption
              brand={item.brand!}
              brandSourceCategory={item.category!}
              onValueChange={handleSelectBrand}
              isSelected={item.isSelected}
            />
          );
        case 'header':
          return (
            <div className="h-9 mt-2 px-2 animate-fade-in">
              <Label className="font-bold">{item.title}</Label>
            </div>
          );
        case 'separator':
          return <div className="w-full bg-tertiary h-[1px]" />;
        case 'show-all':
          return (
            <div
              className="rounded-md flex h-9 cursor-pointer items-center justify-center px-2 text-sm text-primary hover:underline"
              onClick={() => setShowAllWatchlist(true)}
            >
              <Typography className="text-xs">Show All</Typography>
            </div>
          );
        case 'loading':
          return <LoadingItem setRef={ref} />;
        default:
          return null;
      }
    },
    [handleSelectBrand, ref],
  );

  // Effects
  useEffect(() => {
    if (inView && activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      activeQuery.fetchNextPage();
    }
  }, [inView, activeQuery]);

  useEffect(() => {
    if (open && parentRef.current) {
      parentRef.current.scrollTop = 0;
    }
  }, [open]);

  useEffect(() => {
    if (open && renderedCategories.includes('other')) {
      const hasNoData = !initialQuery.data?.pages?.length;
      if (hasNoData && !initialQuery.isFetching) {
        initialQuery.refetch();
      }
    }
  }, [open, initialQuery, renderedCategories]);

  return (
    <Select open={open} onOpenChange={handleOpenChange}>
      <SelectTrigger
        disabled={disabled || !hasOptionsLoaded}
        className={cn(
          'disabled:opacity-50 w-full h-10 border border-input rounded-lg bg-background px-3 flex justify-between items-center text-left shadow-input',
          className,
        )}
      >
        {value ? (
          <div className="flex items-center gap-2">
            <Typography className="text-sm">
              {renderSelectedOption()}
            </Typography>
          </div>
        ) : (
          <Typography className="text-sm text-tertiary">
            {!disabled && !hasOptionsLoaded
              ? 'Brands Loading...'
              : 'Select a brand'}
          </Typography>
        )}
      </SelectTrigger>
      <SelectContent className="p-1">
        <Input
          placeholder="Search for brands..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <div
          ref={parentRef}
          className={`mt-2 max-h-[${MAX_HEIGHT}px] overflow-y-auto scrollbar-hidden min-w-64`}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer
              .getVirtualItems()
              .map((virtualRow: VirtualizerItem) => {
                const item = virtualItems[virtualRow.index];
                return (
                  <div
                    key={virtualRow.index}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {renderVirtualItem(item)}
                  </div>
                );
              })}
          </div>
        </div>
      </SelectContent>
    </Select>
  );
}
